import React, { useEffect, useState } from "react";
import useSWR from "swr";
import axios from "axios";
import { useDebouncedCallback } from "use-debounce";
import { useSearchParams } from "react-router-dom";
import { Button, Input, Modal, Form, message } from "antd";
import { SearchOutlined } from "@ant-design/icons";

import TableComponent from "../../components/Table/TableComponent";
import {
  generateSearchParamsGetter,
  getNewSearchParamStringWithArgs,
  getSearchParams,
  isPositiveInteger,
} from "../../utils/helpers";
import AdminAppLayout from "../../Layouts/AdminLayout";
import { getColumns } from "./columns";
import CategoryForm from "./form";

import { BASE_URL } from "../../../Constants/EnvironmentVariableProvider";
import {
  ADD_CATEGORY_API,
  DELETE_CATEGORY_API,
  EDIT_CATEGORY_API,
  CATEGORY_FILTER_DATA_API,
} from "../../../API/paths";
import { DATAGRID_DEFAULT_PAGE_SIZE } from "../../../Constants/ApplicationConstants";

const Category = () => {
  let [spObj, setSearchParams] = useSearchParams({
    page: "1",
    pageSize: DATAGRID_DEFAULT_PAGE_SIZE.toString(),
  });
  spObj.get('page') === null && spObj.set('page', '1');
  spObj.get('pageSize') === null && spObj.set('pageSize', DATAGRID_DEFAULT_PAGE_SIZE.toString());

  const [isEdit, setIsEdit] = useState(false);

  let [id, page, pageSize, filterName] = getSearchParams(spObj);

  const getNewSearchParamString = generateSearchParamsGetter(spObj);

  const mutateSearchParams = () => setSearchParams(getNewSearchParamString());

  const debounceFetcher = useDebouncedCallback((search) => {
    setSearchParams(
      getNewSearchParamStringWithArgs({ page: 1, pageSize, search })
    );
  }, 600);

  const [form] = Form.useForm();

  let url = BASE_URL + CATEGORY_FILTER_DATA_API + getNewSearchParamString();
  const {
    data,
    isValidating: loading,
    mutate,
  } = useSWR({
    url: url,
  });
  const openModal = (id) => {
    form.resetFields();
    setSearchParams(`${getNewSearchParamString()}&id=${id || "create"}`);
  };

  const closeModal = () => {
    form.resetFields();
    mutateSearchParams();
  };

  useEffect(() => {
    if (mutate) mutate();
  }, [filterName, pageSize, page, mutate]);

  const deleteCategory = (id, Reward_Quantity) => {
    if (Reward_Quantity > 0) {
      message.error(
        "The category cannot be deleted since it has reward items associated with it."
      );
      return;
    }
    mutate(async () => {
      try {
        let response = await axios.put(DELETE_CATEGORY_API + `?Id=${id}`);
        if (response?.data?.success) {
          message.success(response?.data?.message);
        } else {
          message.error(response?.data?.message);
        }
      } catch (e) {
        console.log(e);
      }
    });
  };

  const handleSubmit = (values) => {
    const updateData = {
      ARLC_KeyID: id,
      ARLC_Title: values.ARLC_Title,
    };
    mutate(async () => {
      try {
        if (id === "create") {
          const response = await axios.post(ADD_CATEGORY_API, values);
          message.success(response?.data?.message);
          closeModal();
        } else {
          await axios.put(EDIT_CATEGORY_API, updateData);
          message.success("Category updated successfully.");
        }
      } catch (e) {
        console.log(e);
        message.error(e?.response?.data?.message);
      }
    });
  };

  const onPaginationChange = async (page, pageSize) => {
    setSearchParams(
      getNewSearchParamStringWithArgs({ page, pageSize, search: filterName })
    );
  };

  const columns = getColumns({
    deleteCategory,
    openModal,
    setIsEdit,
  });

  const handleSearch = (e) => {
    debounceFetcher(e.target.value);
    setSearchState(e.target.value);
  };

  const [searchState, setSearchState] = useState(filterName ? filterName : '')
  useEffect(() => {
    setSearchState(filterName)
  }, [filterName])

  return (
    <AdminAppLayout>
      <div
        style={{
          paddingLeft: 60,
          paddingRight: 60,
          marginTop: 56,
          marginBottom: 20,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginTop: 72,
            marginBottom: 28,
          }}
        >
          <Input
            style={{ width: 480 }}
            size="large"
            placeholder="Search Category..."
            defaultValue={filterName}
            value={searchState}
            onChange={handleSearch}
            allowClear
            prefix={
              <SearchOutlined
                style={{
                  color: "rgba(0,0,0,.45)",
                }}
              />
            }
          />

          <Button type="primary" onClick={() => openModal()}>
            + Add New Category
          </Button>
        </div>

        <div>
          <TableComponent
            loading={loading}
            data={data?.result}
            columns={columns}
            totalRecords={data?.totalRecords}
            border
            pagination={{
              totalPage: data?.totalPages * pageSize,
              pageSize: pageSize,
            }}
            onPaginationChange={onPaginationChange}
            isServerPagination // true for serverSide pagination
          />
        </div>

        {/* Add New category model component */}
        <Modal
          open={id === "create" || isPositiveInteger(id)}
          onCancel={() => {
            closeModal();
            form.resetFields();
          }}
          centered
          width={666}
          footer={null}
        >
          <CategoryForm
            id={id}
            form={form}
            handleSubmit={handleSubmit}
            listLoading={loading}
            closeModal={closeModal}
            isEdit={isEdit}
          />
        </Modal>
      </div>
    </AdminAppLayout>
  );
};
export default Category;
